import { createAsyncThunk } from '@reduxjs/toolkit'
import RestClient from '../../../../services/client/RestClient'
import Configuration from '../../../../services/api/Configuration'
import { isNotEmpty } from '../../../../utils/helpers'

const getUsersApi = Configuration.Sys_Admin_Users_Endpoints.list
const resetPasswordRequestApi = Configuration.Sys_Admin_Users_Endpoints.resetPasswordRequest
const getCompaniesLookupApi = Configuration.Company_Endpoints.lookup

export const sysAdminUsersResetAlertHandler = createAsyncThunk(
  'sysAdminUsers/sysAdminUsersResetAlertHandler',
  async () => ({}),
)

export const retrieveUsers = createAsyncThunk('sysAdminUsers/getUsers', async () => {
  const response = await RestClient.getRequest(`${getUsersApi}`)
  return response.data
})

export const retrieveCompanies = createAsyncThunk('companies/lookups', async () => {
  const response = await RestClient.getRequest(`${getCompaniesLookupApi}`)
  return response.data
})

export const retrieveUserByID = createAsyncThunk(
  'sysAdminUsers/getUserById',
  async ({ queries }) => {
    const res = await RestClient.getRequest(`${getUsersApi}/${queries.id}`)
    return res.data
  },
)

export const filterBySummary = createAsyncThunk(
  'sysAdminUsers/usersBySummary',
  async ({ queries }, { getState }) => {
    const { sysAdminUsers } = getState()
    const response = await RestClient.getRequest(
      `${getUsersApi}?search=${sysAdminUsers.activeSearch}&data_status=${queries}&sort_by=${
        sysAdminUsers.activeSort.key || ''
      }&user_types=${sysAdminUsers.activeUserTypes}&company_ids=${sysAdminUsers.companyIDs}`,
    )
    return { data: response.data, summary: queries }
  },
)

export const filterByUserType = createAsyncThunk(
  'sysAdminUsers/usersByType',
  async ({ queries }, { getState }) => {
    const { sysAdminUsers } = getState()
    const response = await RestClient.getRequest(
      `${getUsersApi}?user_types=${queries}&company_ids=${sysAdminUsers.companyIDs}`,
    )
    return {
      data: response.data,
      userTypes: queries,
    }
  },
)
export const filterByCompanyID = createAsyncThunk(
  'sysAdminUsers/usersByCompanyID',
  async ({ queries }, { getState }) => {
    const { sysAdminUsers } = getState()
    const response = await RestClient.getRequest(
      `${getUsersApi}?&user_types=${sysAdminUsers.activeUserTypes}&company_ids=${queries}`,
    )
    return {
      data: response.data,
      companyIDs: queries,
    }
  },
)

export const searchUsers = createAsyncThunk(
  'sysAdminUsers/searchUsers',
  async ({ queries }, { getState }) => {
    const { sysAdminUsers } = getState()
    const response = await RestClient.getRequest(
      `${getUsersApi}?search=${queries || ''}&data_status=${sysAdminUsers.activeSummary}&sort_by=${
        sysAdminUsers.activeSort.key || ''
      }&user_types=${sysAdminUsers.activeUserTypes}&company_ids=${sysAdminUsers.companyIDs}`,
    )
    return {
      data: response.data,
      search: queries || '',
    }
  },
)

export const updateSelectedUserStore = createAsyncThunk(
  'sysAdminUsers/updateSelectedUser',
  async ({ queries }) => queries,
)

export const updateTenantID = createAsyncThunk(
  'sysAdminUsers/updateTenantID',
  async ({ queries }) => queries,
)

export const usersSortBy = createAsyncThunk(
  'sysAdminUsers/usersSortBy',
  async ({ queries }, { getState }) => {
    const { sysAdminUsers } = getState()
    const response = await RestClient.getRequest(
      `${getUsersApi}?search=${sysAdminUsers.activeSearch}&data_status=${sysAdminUsers.activeSummary}&sort_by=${queries.key}&user_types=${sysAdminUsers.activeUserTypes}&company_ids=${sysAdminUsers.companyIDs}`,
    )
    return { data: response.data, sort: queries }
  },
)

export const usersPagination = createAsyncThunk(
  'sysAdminUsers/usersPagination',
  async ({ queries }, { getState }) => {
    const { sysAdminUsers } = getState()
    const response = await RestClient.getRequest(
      `${getUsersApi}?search=${sysAdminUsers.activeSearch}&page_number=${
        queries.pageNumber
      }&data_status=${sysAdminUsers.activeSummary}&sort_by=${
        sysAdminUsers.activeSort.key || ''
      }&user_types=${sysAdminUsers.activeUserTypes}&company_ids=${sysAdminUsers.companyIDs}`,
    )
    return { data: response.data, pageNumber: queries.pageNumber }
  },
)

export const createUsers = createAsyncThunk(
  'sysAdminUsers/createUsers',
  async ({ queries }, { rejectWithValue, getState }) => {
    const titleId = { title_id: queries.updatedUser?.title?.id }
    let employee = null

    if (isNotEmpty(queries.updatedUser.employee)) {
      employee = {
        code: queries.updatedUser.employee,
        departments: queries.updatedUser.departments,
      }
    }

    const updatedUserClone = { ...queries.updatedUser }
    delete updatedUserClone.title

    const newQuery = {
      ...updatedUserClone,
      ...titleId,
      employee: queries.updatedUser.employee?.id ? queries.updatedUser.employee : employee,
    }

    const res = await RestClient.postRequest(`${getUsersApi}`, newQuery, {
      headers: {
        tenant_id: newQuery.tenant_id,
      },
    })

    if (res.status >= 300) {
      return rejectWithValue(res)
    }

    const { language } = getState()

    return {
      severity: 'success',
      message: language?.translations?.['User has been added successfully'],
    }
  },
)

export const editUser = createAsyncThunk(
  'sysAdminUsers/editUser',
  async ({ queries }, { rejectWithValue, getState }) => {
    const titleId = { title_id: queries.updatedUser?.title?.id }
    let employee = null

    if (isNotEmpty(queries.updatedUser.employee)) {
      employee = {
        code: queries.updatedUser.employee,
        departments: queries.updatedUser.departments,
      }
    }

    const updatedUserClone = { ...queries.updatedUser }
    delete updatedUserClone.title

    const newQuery = {
      ...updatedUserClone,
      ...titleId,
      employee: queries.updatedUser.employee?.id ? queries.updatedUser.employee : employee,
    }

    const res = await RestClient.putRequest(`${getUsersApi}/${queries.updatedUser.id}`, newQuery, {
      headers: {
        tenant_id: queries.updatedUser.tenant_id,
      },
    })

    if (res.status >= 300) {
      return rejectWithValue(res)
    }

    const { language } = getState()

    return {
      severity: 'success',
      message: language?.translations?.['User has been updated successfully'],
    }
  },
)

export const userChangeStatus = createAsyncThunk(
  'sysAdminUsers/changeStatus',
  async ({ queries }) => {
    let body
    if (queries.title === 'Active') body = { active: true }
    else if (queries.title === 'Disabled') {
      body = { active: false }
    }
    const response = await RestClient.putRequest(`${getUsersApi}/${queries.data.id}`, body)
    return response.data
  },
)

export const resetUserPasswordRequest = createAsyncThunk(
  'sysAdminUsers/resetUserPasswordRequest',
  async ({ queries }) => {
    const response = await RestClient.postRequest(
      `${getUsersApi}/${queries.data.id}/${resetPasswordRequestApi}`,
    )
    return response.data
  },
)
